import { useTheme } from "@/theme/ThemeProvider";
import { BrandLogo } from "@/components/theme/BrandIdentity";
import loginStoreImage from "@/assets/login-store.jpg";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/pos-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ScanLine, Loader2, Store } from "lucide-react";

export default function Login() {
  const { current } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Selamat datang, ${u.name}`);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="masivi-login min-h-screen flex bg-ui-canvas text-ui-text">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-ui-card p-12 relative overflow-hidden border-r border-ui-line">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              `url(${loginStoreImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-brand-button flex items-center justify-center">
            <BrandLogo className="h-7 w-7" />
          </div>
          <div>
            <div className="font-heading text-2xl font-extrabold tracking-tight">{current.branding.name}</div>
            <div className="text-xs text-ui-muted uppercase tracking-widest">Point of Sale System</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-heading text-4xl font-extrabold leading-tight mb-4">
            Kasir modern untuk <span className="text-brand-readable">tablet</span> & komputer.
          </h1>
          <p className="text-ui-muted text-base max-w-md leading-relaxed">
            Transaksi cepat dengan layar sentuh, level harga dinamis (Eceran, Grosir, Member),
            pembayaran Tunai / Transfer / QRIS / Split, dan struk thermal 80mm.
          </p>
        </div>
        <div className="relative z-10 text-xs text-ui-muted">© 2026 {current.branding.name}</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-pop">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="h-11 w-11 rounded-xl bg-brand-button flex items-center justify-center">
              <BrandLogo className="h-6 w-6" />
            </div>
            <div className="font-heading text-xl font-extrabold">{current.branding.name}</div>
          </div>
          <h2 className="font-heading text-3xl font-bold mb-1">Masuk</h2>
          <p className="text-ui-muted mb-8 text-sm">Masuk sebagai Admin atau Kasir untuk mulai berjualan.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-ui-muted">Email</Label>
              <Input
                id="email"
                autoComplete="username"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@toko.com"
                className="h-12 bg-ui-card border-ui-line text-base"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-ui-muted">Password</Label>
              <Input
                id="password"
                autoComplete="current-password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 bg-ui-card border-ui-line text-base"
                required
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full h-12 text-base font-bold bg-brand-button hover:bg-brand-button text-ui-text"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Masuk"}
            </Button>
          </form>


        </div>
      </div>
    </div>
  );
}
